<template>
  <v-container fluid class="invitation-approvals pa-3 pa-md-6">
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between mb-6">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 elevation-2">
          <v-icon dark>mdi-email-check-outline</v-icon>
        </v-avatar>
        <div>
          <h1 class="text-h5 text-md-h4 font-weight-bold mb-1">
            {{ $t('invitation_approvals') }}
          </h1>
          <p class="grey--text text--darken-1 mb-0">
            {{ $t('invitation_approvals_description') }}
          </p>
        </div>
      </div>
      <v-btn
        color="primary"
        outlined
        rounded
        class="mt-4 mt-sm-0"
        :loading="loading"
        @click="loadInvitations"
      >
        <v-icon left>mdi-refresh</v-icon>
        {{ $t('refresh') }}
      </v-btn>
    </div>

    <v-alert v-if="errorMessage" type="error" outlined dismissible @input="errorMessage = ''">
      {{ errorMessage }}
    </v-alert>

    <v-row class="mb-2">
      <v-col cols="12" sm="4">
        <v-card outlined class="summary-card summary-card--pending">
          <v-card-text class="d-flex align-center">
            <v-avatar color="orange lighten-5" size="46" class="mr-4">
              <v-icon color="orange darken-2">mdi-clock-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ pendingInvitations.length }}</div>
              <div class="grey--text text--darken-1">{{ $t('pending_review') }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="summary-card summary-card--approved">
          <v-card-text class="d-flex align-center">
            <v-avatar color="green lighten-5" size="46" class="mr-4">
              <v-icon color="green darken-1">mdi-check-circle-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ approvedCount }}</div>
              <div class="grey--text text--darken-1">{{ $t('approved_current') }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="summary-card summary-card--rejected">
          <v-card-text class="d-flex align-center">
            <v-avatar color="red lighten-5" size="46" class="mr-4">
              <v-icon color="red darken-1">mdi-close-circle-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ rejectedCount }}</div>
              <div class="grey--text text--darken-1">{{ $t('rejected_current') }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="rounded-lg elevation-2 overflow-hidden">
      <v-tabs v-model="activeTab" color="primary" show-arrows>
        <v-tab>
          <v-badge
            :content="pendingInvitations.length"
            :value="pendingInvitations.length > 0"
            color="orange darken-1"
            overlap
            class="mr-3"
          >
            <v-icon left>mdi-inbox-arrow-down-outline</v-icon>
          </v-badge>
          {{ $t('pending_invitations') }}
        </v-tab>
        <v-tab>
          <v-icon left>mdi-history</v-icon>
          {{ $t('reviewed_invitations') }}
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <div class="pa-4">
            <v-text-field
              v-model="pendingSearch"
              :label="$t('search_invitations')"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="table-search mb-4"
            ></v-text-field>

            <v-data-table
              :headers="pendingHeaders"
              :items="pendingInvitations"
              :search="pendingSearch"
              :loading="loadingPending"
              :no-data-text="$t('no_pending_invitations')"
              :no-results-text="$t('no_search_results')"
              :footer-props="footerProps"
              class="invitation-table"
            >
              <template #item.guest_name="{ item }">
                <div class="py-2">
                  <div class="font-weight-medium">{{ item.guest_name || $t('not_available') }}</div>
                  <div class="caption grey--text">{{ item.guest_number_phone || $t('without_phone') }}</div>
                </div>
              </template>
              <template #item.host_name="{ item }">
                <div>{{ item.host_name || $t('not_available') }}</div>
                <div class="caption grey--text">{{ item.house_address || '' }}</div>
              </template>
              <template #item.date="{ item }">
                <div class="text-no-wrap">
                  <v-icon small color="grey" class="mr-1">mdi-calendar-outline</v-icon>
                  {{ formatDate(item.date) }}
                </div>
                <div class="caption grey--text ml-5">{{ formatTime(item.hour) }}</div>
              </template>
              <template #item.destination="{ item }">
                <span>{{ item.destination || $t('without_destination') }}</span>
              </template>
              <template #item.type="{ item }">
                <v-chip small label :color="item.type === 0 ? 'blue lighten-5' : 'purple lighten-5'"
                  :text-color="item.type === 0 ? 'blue darken-2' : 'purple darken-2'">
                  {{ invitationType(item.type) }}
                </v-chip>
              </template>
              <template #item.actions="{ item }">
                <div class="d-flex justify-end text-no-wrap">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn icon color="grey darken-1" v-bind="attrs" v-on="on" @click="showDetails(item)">
                        <v-icon>mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('view_details') }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn icon color="success" v-bind="attrs" v-on="on" @click="openApproveDialog(item)">
                        <v-icon>mdi-check-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('approve') }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn icon color="error" v-bind="attrs" v-on="on" @click="openRejectDialog(item)">
                        <v-icon>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('reject') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex flex-column flex-sm-row mb-4">
              <v-text-field
                v-model="reviewedSearch"
                :label="$t('search_invitations')"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="table-search mr-sm-3 mb-3 mb-sm-0"
              ></v-text-field>
              <v-select
                v-model="reviewedStatus"
                :items="statusOptions"
                outlined
                dense
                hide-details
                class="status-filter"
              ></v-select>
            </div>

            <v-data-table
              :headers="reviewedHeaders"
              :items="filteredReviewedInvitations"
              :search="reviewedSearch"
              :loading="loadingReviewed"
              :no-data-text="$t('no_reviewed_invitations')"
              :no-results-text="$t('no_search_results')"
              :footer-props="footerProps"
              class="invitation-table"
            >
              <template #item.guest_name="{ item }">
                <div class="py-2">
                  <div class="font-weight-medium">{{ item.guest_name || $t('not_available') }}</div>
                  <div class="caption grey--text">{{ item.guest_phone || $t('without_phone') }}</div>
                </div>
              </template>
              <template #item.date="{ item }">
                <div class="text-no-wrap">{{ formatDate(item.date) }}</div>
                <div class="caption grey--text">{{ formatTime(item.hour) }}</div>
              </template>
              <template #item.approval_status="{ item }">
                <v-chip small dark :color="statusColor(item.approval_status)">
                  <v-icon left small>{{ statusIcon(item.approval_status) }}</v-icon>
                  {{ statusText(item.approval_status) }}
                </v-chip>
              </template>
              <template #item.reviewed_at="{ item }">
                {{ formatDateTime(item.reviewed_at) }}
              </template>
              <template #item.actions="{ item }">
                <v-btn icon color="grey darken-1" @click="showDetails(item)">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="detailsDialog" max-width="680" scrollable>
      <v-card v-if="selectedInvitation">
        <v-toolbar color="primary" dark flat>
          <v-toolbar-title>{{ $t('invitation_details') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-5">
            <v-avatar color="primary lighten-5" size="52" class="mr-3">
              <v-icon color="primary">mdi-account-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ selectedInvitation.guest_name }}</div>
              <div class="grey--text">{{ guestContact(selectedInvitation) }}</div>
            </div>
          </div>
          <v-row dense>
            <v-col v-for="detail in invitationDetails" :key="detail.label" cols="12" sm="6">
              <div class="detail-field pa-3 mb-2">
                <div class="caption grey--text text--darken-1">{{ detail.label }}</div>
                <div class="body-1 font-weight-medium">{{ detail.value }}</div>
              </div>
            </v-col>
          </v-row>
          <v-alert v-if="selectedInvitation.rejection_reason" type="error" text class="mt-3 mb-0">
            <strong>{{ $t('rejection_reason') }}:</strong> {{ selectedInvitation.rejection_reason }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t('approve_invitation') }}</v-card-title>
        <v-card-text>
          {{ $t('approve_invitation_confirmation', { guest: actionInvitation ? actionInvitation.guest_name : '' }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="actionLoading" @click="closeActionDialogs">{{ $t('cancel') }}</v-btn>
          <v-btn color="success" dark :loading="actionLoading" @click="confirmApprove">
            <v-icon left>mdi-check</v-icon>{{ $t('approve') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t('reject_invitation') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('reject_invitation_confirmation', { guest: actionInvitation ? actionInvitation.guest_name : '' }) }}</p>
          <v-textarea
            v-model="rejectionReason"
            :label="$t('rejection_reason_optional')"
            :counter="255"
            maxlength="255"
            outlined
            rows="3"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="actionLoading" @click="closeActionDialogs">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" dark :loading="actionLoading" @click="confirmReject">
            <v-icon left>mdi-close</v-icon>{{ $t('reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import {
  approveInvitation,
  getCurrentInvitations,
  getPendingInvitations,
  rejectInvitation
} from '@/services/invitationApprovals'

const normalizeResponseList = response => {
  const data = response && response.data
  if (Array.isArray(data)) return data
  return data && Array.isArray(data.results) ? data.results : []
}

export default {
  name: 'InvitationApprovals',
  data: () => ({
    activeTab: 0,
    pendingInvitations: [],
    reviewedInvitations: [],
    pendingSearch: '',
    reviewedSearch: '',
    reviewedStatus: 'all',
    loadingPending: false,
    loadingReviewed: false,
    errorMessage: '',
    detailsDialog: false,
    approveDialog: false,
    rejectDialog: false,
    selectedInvitation: null,
    actionInvitation: null,
    actionLoading: false,
    rejectionReason: '',
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    footerProps: {
      'items-per-page-options': [10, 25, 50, 100]
    }
  }),
  computed: {
    loading () {
      return this.loadingPending || this.loadingReviewed
    },
    approvedCount () {
      return this.reviewedInvitations.filter(item => item.approval_status === 1).length
    },
    rejectedCount () {
      return this.reviewedInvitations.filter(item => item.approval_status === 2).length
    },
    filteredReviewedInvitations () {
      if (this.reviewedStatus === 'all') return this.reviewedInvitations
      const status = this.reviewedStatus === 'approved' ? 1 : 2
      return this.reviewedInvitations.filter(item => item.approval_status === status)
    },
    statusOptions () {
      return [
        { text: this.$t('all_statuses'), value: 'all' },
        { text: this.$t('approved'), value: 'approved' },
        { text: this.$t('rejected'), value: 'rejected' }
      ]
    },
    pendingHeaders () {
      return [
        { text: this.$t('guest'), value: 'guest_name' },
        { text: this.$t('host'), value: 'host_name' },
        { text: this.$t('invitation_date'), value: 'date' },
        { text: this.$t('destination'), value: 'destination' },
        { text: this.$t('invitation_type'), value: 'type' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    reviewedHeaders () {
      return [
        { text: this.$t('guest'), value: 'guest_name' },
        { text: this.$t('host'), value: 'host_name' },
        { text: this.$t('invitation_date'), value: 'date' },
        { text: this.$t('status'), value: 'approval_status' },
        { text: this.$t('reviewed_at'), value: 'reviewed_at' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    invitationDetails () {
      const item = this.selectedInvitation
      if (!item) return []
      return [
        { label: this.$t('host'), value: item.host_name || this.$t('not_available') },
        { label: this.$t('house'), value: item.house_address || this.$t('not_available') },
        { label: this.$t('subject'), value: item.subject || this.$t('not_available') },
        { label: this.$t('destination'), value: item.destination || this.$t('without_destination') },
        { label: this.$t('invitation_date'), value: `${this.formatDate(item.date)} · ${this.formatTime(item.hour)}` },
        { label: this.$t('invitation_type'), value: this.invitationType(item.type) },
        { label: this.$t('expiration'), value: item.expiration ? this.formatDate(item.expiration) : this.$t('not_applicable') },
        { label: this.$t('vehicle'), value: item.vehicle ? this.$t('yes') : this.$t('no') },
        { label: this.$t('supplier'), value: item.supplier ? this.$t('yes') : this.$t('no') },
        { label: this.$t('notes'), value: item.notes || this.$t('without_notes') }
      ]
    }
  },
  created () {
    this.loadInvitations()
  },
  methods: {
    async loadInvitations () {
      this.errorMessage = ''
      this.loadingPending = true
      this.loadingReviewed = true
      const results = await Promise.allSettled([
        getPendingInvitations(),
        getCurrentInvitations()
      ])

      if (results[0].status === 'fulfilled') {
        this.pendingInvitations = normalizeResponseList(results[0].value)
      } else {
        this.errorMessage = this.apiError(results[0].reason)
      }

      if (results[1].status === 'fulfilled') {
        this.reviewedInvitations = normalizeResponseList(results[1].value)
          .filter(item => item.approval_status === 1 || item.approval_status === 2)
      } else if (!this.errorMessage) {
        this.errorMessage = this.apiError(results[1].reason)
      }

      this.loadingPending = false
      this.loadingReviewed = false
    },
    showDetails (item) {
      this.selectedInvitation = item
      this.detailsDialog = true
    },
    openApproveDialog (item) {
      this.actionInvitation = item
      this.approveDialog = true
    },
    openRejectDialog (item) {
      this.actionInvitation = item
      this.rejectionReason = ''
      this.rejectDialog = true
    },
    closeActionDialogs () {
      this.approveDialog = false
      this.rejectDialog = false
      this.actionInvitation = null
      this.rejectionReason = ''
    },
    async confirmApprove () {
      if (!this.actionInvitation) return
      this.actionLoading = true
      try {
        await approveInvitation(this.actionInvitation.id)
        this.showMessage(this.$t('invitation_approved_successfully'), 'success')
        this.closeActionDialogs()
        await this.loadInvitations()
      } catch (error) {
        this.showMessage(this.apiError(error), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    async confirmReject () {
      if (!this.actionInvitation) return
      this.actionLoading = true
      try {
        await rejectInvitation(this.actionInvitation.id, this.rejectionReason.trim())
        this.showMessage(this.$t('invitation_rejected_successfully'), 'success')
        this.closeActionDialogs()
        await this.loadInvitations()
      } catch (error) {
        this.showMessage(this.apiError(error), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    showMessage (message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    apiError (error) {
      const response = error && error.response
      const data = response && response.data
      if (data && data.error) return data.error
      if (data && data.detail) return data.detail
      if (response && response.status === 403) return this.$t('invitation_approval_forbidden')
      return this.$t('invitation_loading_error')
    },
    formatDate (value) {
      if (!value) return this.$t('not_available')
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatTime (value) {
      return value ? String(value).slice(0, 5) : this.$t('not_available')
    },
    formatDateTime (value) {
      if (!value) return this.$t('not_available')
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return new Intl.DateTimeFormat(this.$i18n.locale === 'es' ? 'es-MX' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(date)
    },
    invitationType (type) {
      return type === 0 ? this.$t('single_invitation') : this.$t('recurring_invitation')
    },
    statusText (status) {
      if (status === 1) return this.$t('approved')
      if (status === 2) return this.$t('rejected')
      return this.$t('pending')
    },
    statusColor (status) {
      if (status === 1) return 'success'
      if (status === 2) return 'error'
      return 'warning'
    },
    statusIcon (status) {
      if (status === 1) return 'mdi-check-circle-outline'
      if (status === 2) return 'mdi-close-circle-outline'
      return 'mdi-clock-outline'
    },
    guestContact (item) {
      return item.guest_phone || item.guest_number_phone || item.guest_email || this.$t('without_contact')
    }
  }
}
</script>

<style scoped>
.invitation-approvals {
  max-width: 1500px;
}

.summary-card {
  height: 100%;
  border-left-width: 4px !important;
  border-radius: 10px !important;
}

.summary-card--pending {
  border-left-color: #fb8c00 !important;
}

.summary-card--approved {
  border-left-color: #43a047 !important;
}

.summary-card--rejected {
  border-left-color: #e53935 !important;
}

.table-search {
  max-width: 430px;
}

.status-filter {
  max-width: 220px;
}

.detail-field {
  height: 100%;
  border-radius: 8px;
  background: #f6f7f9;
}

@media (max-width: 599px) {
  .table-search,
  .status-filter {
    max-width: none;
  }
}
</style>
